// ─────────────────────────────────────────────────────────────────────────────
// upscQuestion.js — turns a question's raw text into a structured UPSC layout.
// ONE parser shared by the PDF export (prelimsPdf.js) and the on-screen card
// (UPSCQuestion.jsx), so the app and the downloaded paper can never disagree.
//
// Question kinds:
//   simple      plain stem, no sub-items
//   items       "Consider the following statements : I. … II. …" (I/II/III or 1/2/3)
//   statements  "Statement I : … Statement II : …"  (also Assertion (A) / Reason (R))
//   table       match-the-following: pipe tables, "pairs/information" lists with a
//               separator (:, -, –, →), or List-I / List-II
// ─────────────────────────────────────────────────────────────────────────────

// ═════════════════════════════════════════════════════════════════════════════
// 1. TEXT CLEANING
// ═════════════════════════════════════════════════════════════════════════════

// Light clean for ON-SCREEN display: keeps every character (₹, →, Hindi …).
export function tidy(value) {
  return String(value ?? '')
    .replace(/\\n/g, '\n') // Gemini sometimes double-escapes line breaks
    .replace(/\r\n?/g, '\n')
    .replace(/\*\*|__|`/g, '');
}

// PDF clean: jsPDF's built-in Times font only supports WinAnsi, so strip/replace the rest.
export function clean(value) {
  return tidy(value)
    .replace(/[→⇒]/g, '->')
    .replace(/₹\s*/g, 'Rs ')
    .replace(/[\u00A0\u2000-\u200B\u202F]/g, ' ')
    .replace(/[^\n\x20-\x7E\xA1-\xFF\u2013\u2014\u2018\u2019\u201C\u201D\u2022\u2026\u20AC\u2122]/g, '');
}
export const flatten = (s) => s.replace(/\s+/g, ' ').trim();
export const spaceColon = (s) => s.replace(/\s*:\s*$/, ' :'); // UPSC prints "statements :"
export const stripOptionLabel = (s) => s.replace(/^\s*(?:\([A-Da-d]\)|[A-Da-d][.)])\s+/, '');

// ═════════════════════════════════════════════════════════════════════════════
// 2. QUESTION PARSER
// ═════════════════════════════════════════════════════════════════════════════

const ROMAN = { I: 1, II: 2, III: 3, IV: 4, V: 5, VI: 6, VII: 7, VIII: 8, IX: 9, X: 10 };

// Phrases that start the "closing question" after the statements/items.
// STRONG ones are unambiguous, so they are recognised even when the generated
// text has no full stop before them ("… Venture Capital How many of the above…").
// WEAK ones ("Which of the…") are only treated as a tail after sentence punctuation.
const TAIL_STRONG = [
  'How many of the', 'In how many', 'Select the correct', 'Select the right', 'Select the answer',
  'Using the code', 'Consider the above', 'Which of the statements given above',
  'Which of the pairs given above', 'Which of the above', 'Which one of the above',
  'Which one of the following is correct', 'Codes?\\s*:',
];
const TAIL_WEAK = [
  'Which one of the', 'Which ones of the', 'Which of the', 'Which among', 'Which one among',
  'Which statement', 'Select the', 'Choose the',
];
const spaced = (list) => list.map((s) => s.replace(/ /g, '\\s+')).join('|');
const TAIL_SRC =
  `(^|[.?!:;)\\]”’"']\\s+|\\s+(?=(?:${spaced(TAIL_STRONG)})))(?:${spaced(TAIL_STRONG)}|${spaced(TAIL_WEAK)})`;
const tailRegex = () => new RegExp(TAIL_SRC, 'gi');

function findTailStart(text, from) {
  const re = tailRegex();
  re.lastIndex = from;
  const m = re.exec(text);
  return m ? m.index + m[1].length : -1;
}

// "Which … correct? Select the correct answer …" → two paragraphs
function splitTail(text) {
  const re = tailRegex();
  const cuts = [0];
  let m;
  while ((m = re.exec(text))) {
    const at = m.index + m[1].length;
    if (at > 0 && /\S/.test(m[1])) cuts.push(at); // cut only after sentence punctuation
  }
  return cuts.map((c, i) => text.slice(c, cuts[i + 1]).trim()).filter(Boolean);
}

// Finds a 1,2,3… (or I,II,III… / A,B,C…) sequence of list markers in the text.
const MARKERS = {
  num:   { re: /(^|\s)(\d{1,2})[.)]\s+(?=\S)/g,    value: (t) => parseInt(t, 10) },
  roman: { re: /(^|\s)([IVX]{1,4})[.)]\s+(?=\S)/g, value: (t) => ROMAN[t] || 0 },
  alpha: { re: /(^|\s)([A-H])[.)]\s+(?=\S)/g,      value: (t) => t.charCodeAt(0) - 64 },
};

function findChain(text, kind) {
  const { re, value } = MARKERS[kind];
  re.lastIndex = 0;
  const found = [];
  let m;
  while ((m = re.exec(text))) {
    found.push({ token: m[2], value: value(m[2]), start: m.index + m[1].length, end: m.index + m[0].length });
  }
  let best = [];
  found.forEach((first, i) => {
    if (first.value !== 1) return;
    const chain = [first];
    for (let j = i + 1; j < found.length; j++) {
      if (found[j].value === chain.length + 1) chain.push(found[j]);
    }
    if (chain.length > best.length) best = chain;
  });
  return best.length >= 2 ? best : [];
}

const chainTexts = (text, chain, end) =>
  chain.map((c, i) => text.slice(c.end, i + 1 < chain.length ? chain[i + 1].start : end).trim());

// ── tables ───────────────────────────────────────────────────────────────────

const LABEL_CELL = /^(\d{1,2}|[IVX]{1,4}|[A-H])[.)]?$/;

// rows: array of string[] (first row = header)
function parsePipeTable(raw) {
  const lines = raw.split('\n').map((l) => l.trim()).filter(Boolean);
  const isRow = (l) => l.includes('|');
  const first = lines.findIndex(isRow);
  if (first < 0) return null;
  let last = first;
  while (last + 1 < lines.length && isRow(lines[last + 1])) last++;
  if (last === first) return null;

  const rows = lines
    .slice(first, last + 1)
    .filter((l) => !/^[\s|:\-]+$/.test(l)) // markdown separator row
    .map((l) => l.replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim()));
  const [header, ...body] = rows;
  if (!body.length) return null;

  // Row labels either sit in their own cell ("I." | …) or prefix the first cell ("I. Directorate | …")
  const PREFIX = /^(\d{1,2}|[IVX]{1,4})[.)]\s+(?=\S)/;
  const ownCell = body.every((r) => LABEL_CELL.test(r[0]));
  const prefixed = !ownCell && body.every((r) => PREFIX.test(r[0])) && !PREFIX.test(header[0]);
  const withDot = (l) => (/[.)]$/.test(l) ? l : `${l}.`);
  const table = {
    header: ownCell && header.length === body[0].length ? header.slice(1) : header,
    rows: body.map((r) => {
      if (ownCell) return { label: withDot(r[0]), cells: r.slice(1) };
      if (prefixed) return { label: withDot(PREFIX.exec(r[0])[1]), cells: [r[0].replace(PREFIX, ''), ...r.slice(1)] };
      return { label: '', cells: r };
    }),
  };
  return {
    kind: 'table',
    stem: flatten(lines.slice(0, first).join(' ')),
    table,
    tail: splitTail(flatten(lines.slice(last + 1).join(' '))),
  };
}

const PAIR_HINT = /\b(pairs?|match(?:ed|ing)?|information|tabulated|table)\b/i;
const CELL_SEPS = [/\s*\|\s*/, /\s*(?:->|=>)\s*/, /\s*:\s+/, /\s+[–—]\s+/, /\s+-\s+/, /\s*;\s+/];

// "Consider the following pairs : 1. A : B 2. C : D" → table
function asPairTable(pre, items) {
  if (!PAIR_HINT.test(pre)) return null;
  for (const sep of CELL_SEPS) {
    const rows = items.map((it) => it.text.split(sep).map((s) => s.trim()).filter(Boolean));
    const n = rows[0].length;
    if (n < 2 || n > 4 || rows.some((r) => r.length !== n)) continue;

    let stem = pre;
    let header = null;
    const hint = /(?:pairs?|information|table|match(?:ed|ing)?)[^:]*:/i.exec(pre);
    if (hint) {
      const cut = hint.index + hint[0].length;
      const head = pre.slice(cut).trim().split(sep).map((s) => s.trim());
      if (head.length === n) { stem = pre.slice(0, cut); header = head; }
    }
    return {
      kind: 'table',
      stem,
      table: { header, rows: items.map((it, i) => ({ label: it.label, cells: rows[i] })) },
    };
  }
  return null;
}

// Match List-I with List-II (A–D against 1–4) → two-column table
function parseListMatch(text) {
  const all = (re) => [...text.matchAll(re)];
  const l2 = all(/List[\s-]*(?:II|2)\b/gi).pop();
  const l1 = all(/List[\s-]*(?:I|1)(?![I\d])\b/gi).filter((m) => l2 && m.index < l2.index).pop();
  if (!l1 || !l2) return null;

  const seg1 = text.slice(l1.index + l1[0].length, l2.index);
  const seg2 = text.slice(l2.index + l2[0].length);
  const c1 = findChain(seg1, 'alpha');
  const c2 = findChain(seg2, 'num');
  if (!c1.length || !c2.length) return null;

  const tailAt = findTailStart(seg2, c2[c2.length - 1].end);
  const t1 = chainTexts(seg1, c1, seg1.length);
  const t2 = chainTexts(seg2, c2, tailAt < 0 ? seg2.length : tailAt);
  const rows = Array.from({ length: Math.max(t1.length, t2.length) }, (_, i) => ({
    label: '',
    cells: [t1[i] ? `${c1[i].token}. ${t1[i]}` : '', t2[i] ? `${c2[i].token}. ${t2[i]}` : ''],
  }));
  const head = (seg, chain, name) => flatten(`${name} ${seg.slice(0, chain[0].start)}`);
  return {
    kind: 'table',
    stem: text.slice(0, l1.index).trim(),
    table: { header: [head(seg1, c1, 'List-I'), head(seg2, c2, 'List-II')], rows },
    tail: tailAt < 0 ? [] : splitTail(seg2.slice(tailAt)),
  };
}

// ── statement blocks ─────────────────────────────────────────────────────────

const BLOCK_SRC =
  '(^|\\s)((?:[Ss]tatements?[\\s-]*(?:III|II|IV|I|V|[1-5])|Assertion\\s*(?:\\(A\\)|A\\b)|Reason\\s*(?:\\(R\\)|R\\b)))\\s*[:\\-–—.]\\s*';

const tidyLabel = (s) =>
  s.replace(/[\s-]+/g, ' ').replace(/^Statements/i, 'Statement')
    .replace(/^Assertion A$/, 'Assertion (A)').replace(/^Reason R$/, 'Reason (R)');

function parseStatementBlocks(text) {
  const re = new RegExp(BLOCK_SRC, 'g');
  const marks = [];
  let m;
  while ((m = re.exec(text))) {
    marks.push({ label: tidyLabel(m[2]), start: m.index + m[1].length, end: m.index + m[0].length });
  }
  if (marks.length < 2) return null;

  const tailAt = findTailStart(text, marks[marks.length - 1].end);
  const bodyEnd = tailAt < 0 ? text.length : tailAt;
  return {
    kind: 'statements',
    stem: text.slice(0, marks[0].start).trim(),
    blocks: marks.map((mk, i) => ({
      label: `${mk.label} :`,
      text: text.slice(mk.end, i + 1 < marks.length ? marks[i + 1].start : bodyEnd).trim(),
    })),
    tail: tailAt < 0 ? [] : splitTail(text.slice(tailAt)),
  };
}

// ── numbered items ───────────────────────────────────────────────────────────

function parseNumbered(text) {
  const num = findChain(text, 'num');
  const roman = findChain(text, 'roman');
  const chain = roman.length > num.length ? roman : num;
  if (!chain.length) return null;

  const pre = text.slice(0, chain[0].start).trim();
  const tailAt = findTailStart(text, chain[chain.length - 1].end);
  const texts = chainTexts(text, chain, tailAt < 0 ? text.length : tailAt);
  const items = chain.map((c, i) => ({ label: `${c.token}.`, text: texts[i] }));
  const tail = tailAt < 0 ? [] : splitTail(text.slice(tailAt));

  const pairs = asPairTable(pre, items);
  return pairs ? { ...pairs, tail } : { kind: 'items', stem: pre, items, tail };
}

// Older cards (esp. Assertion-Reason) carry "(a) … (d) …" inside the question text.
// Options are printed from the options array, so cut the embedded copy off.
export function stripEmbeddedOptions(text) {
  const m = /(^|\s)\(a\)\s+\S[\s\S]*?\s\(b\)\s+\S[\s\S]*?\s\(c\)\s+\S[\s\S]*?\s\(d\)\s+\S/i.exec(text);
  return m ? text.slice(0, m.index).trim() : text;
}

// forPdf: true  → text is also made safe for jsPDF's Times font (see clean())
// forPdf: false → on-screen use, every character is kept
export function parseUPSCQuestion(rawQuestion, { forPdf = false } = {}) {
  const raw = stripEmbeddedOptions((forPdf ? clean : tidy)(rawQuestion));
  const text = flatten(raw);
  return (
    parsePipeTable(raw) ||
    parseListMatch(text) ||
    parseStatementBlocks(text) ||
    parseNumbered(text) ||
    { kind: 'simple', stem: text, tail: [] }
  );
}